export type ProgressVariant = 'default' | 'warning' | 'success' | 'ai';

interface ProgressBarProps {
  /** Valor de 0 a 100 */
  value: number;
  variant?: ProgressVariant;
  /** Largura mínima em px (default 80) */
  minWidth?: number;
  ariaLabel?: string;
  className?: string;
}

const FILL_CLASSES: Record<ProgressVariant, string> = {
  default: 'bg-[var(--brand-600)]',
  warning: 'bg-[var(--warning-500)]',
  success: 'bg-[var(--success-500)]',
  ai:      'bg-[var(--ai-500)]',
};

/**
 * Barra de progresso fina (4px) com 4 variantes semânticas.
 *
 * @example
 * <ProgressBar value={70} variant="default" />
 * <ProgressBar value={42} variant="ai" ariaLabel="Geração 42% concluída" />
 */
export default function ProgressBar({
  value,
  variant = 'default',
  minWidth = 80,
  ariaLabel,
  className,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const fillClass = FILL_CLASSES[variant];

  return (
    <span
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel ?? `${clamped}%`}
      className={`inline-block align-middle h-1 rounded overflow-hidden bg-[var(--gray-100)] dark:bg-[var(--gray-800)] ${className ?? ''}`}
      style={{ minWidth: `${minWidth}px` }}
    >
      <span
        className={`block h-full rounded transition-[width] duration-500 ease-out ${fillClass}`}
        style={{ width: `${clamped}%` }}
      />
    </span>
  );
}
